import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

/**
 * Shopping Cart Item Interface
 */
export interface CartItem {
  id: string; // Unique cart item ID
  productId: string; // TheGame item ID
  name: string;
  price: number;
  quantity: number;
  image: string; // CDN URL for product image
}

/**
 * Shopping Cart Store Interface
 */
export interface CartStore {
  items: CartItem[];
  /** UI only — not persisted */
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
}

/**
 * Shopping Cart Store with Zustand and Persistence Middleware
 */
export const useCartStore = create<CartStore>()(
  persist<CartStore>(
    (set, get) => ({
      items: [] as CartItem[],
      drawerOpen: false,
      setDrawerOpen: (open: boolean) => set({ drawerOpen: open }),
      addItem: (item: Omit<CartItem, 'id'>) => set((state: CartStore) => {
        const existingItem = state.items.find((i: CartItem) => i.productId === item.productId);
        if (existingItem) {
          return {
            items: state.items.map((i) =>
              i.productId === item.productId
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            ),
            drawerOpen: true,
          };
        }
        return {
          items: [...state.items, { ...item, id: uuidv4() }],
          drawerOpen: true,
        };
      }),

      removeItem: (id: string) => set((state: CartStore) => ({
        items: state.items.filter((item) => item.id !== id),
      })),

      updateQuantity: (id: string, quantity: number) => set((state: CartStore) => ({
        items: state.items.map((item: CartItem) =>
          item.id === id ? { ...item, quantity } : item
        ),
      })),

      clearCart: () => set({ items: [] }),

      getTotalPrice: () =>
        get().items.reduce((total: number, item: CartItem) => total + (item.price * item.quantity), 0),

      getTotalItems: () =>
        get().items.reduce((total: number, item: CartItem) => total + item.quantity, 0),
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist `items` (not `drawerOpen` or function fields). Widen for Zustand's persist generic.
      partialize: (state) => ({ items: state.items } as CartStore),
    }
  )
);

/**
 * Optimized selectors for preventing unnecessary re-renders
 */
export const useCartTotal = () => useCartStore((state) => state.getTotalPrice());
export const useCartItems = () => useCartStore((state) => state.items);
export const useCartItemCount = () =>
  useCartStore((state) => state.items.reduce((n, i) => n + i.quantity, 0));
